import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { composeValidators } from 'validators';
import { VARIABLE_PRESENCE_PATTERN } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { getRecurrencePeriodLabels, routes } from '~/constants';
import { serviceBillingModel, serviceTemplateModel } from '~/models';
import { Accounting } from '~/types/accounting';
import { utils } from '~/utils';
import { isServiceTaxable } from '~/utils/salesTax';
export var ColumnId = /*#__PURE__*/ function(ColumnId) {
    ColumnId["Name"] = "Service name";
    ColumnId["IncludedServices"] = "includedServices";
    ColumnId["BillingOccurance"] = "billingOccurance";
    ColumnId["BillingTrigger"] = "billingTrigger";
    ColumnId["Rate"] = "rate";
    ColumnId["Tax"] = "tax";
    ColumnId["QboStatus"] = "qboStatus";
    return ColumnId;
}({});
export const defaultSort = {
    colIndex: 0,
    sort: 'desc'
};
export const sortIds = (headerItems)=>headerItems.map((item)=>item.id);
export const importServicesRoute = {
    id: 'import-services',
    url: routes.importServices,
    shouldShow: (param)=>{
        let { isVendor } = param;
        return isVendor;
    }
};
export async function saveServiceTemplate(param) {
    let { listener, modifiedService, originalService, repo, storePopulator } = param;
    listener.saveStarted();
    try {
        if (originalService) {
            await repo.update(modifiedService);
        } else {
            await repo.create(modifiedService);
        }
        await storePopulator.populateServiceTemplates();
    } catch (err) {
        listener.saveFailed();
        throw err;
    } finally{
        listener.saveFinished();
    }
}
const nameCollator = new Intl.Collator(undefined, {
    sensitivity: 'base'
});
// WHY: anchored to the start — only a COMPLETE leading {{label}} groups first; mid-name tokens and unterminated '{{' sort as plain names (ANCR-3410).
const LEADING_TOKEN_PATTERN = new RegExp("^".concat(VARIABLE_PRESENCE_PATTERN.source));
function leadingTokenLabel(name) {
    const match = LEADING_TOKEN_PATTERN.exec(name.trimStart());
    return match == null ? null : match[0].slice(2, -2);
}
// WHY: raw UTF-16 puts '{' after 'z', exiling {{token}} names to the bottom; tokens sort first by their label, and plain names keep the pre-existing code-point compare byte-for-byte (flag-off invariant, ANCR-3410).
export function sortByName(a, b) {
    const aTokenLabel = leadingTokenLabel(a.name);
    const bTokenLabel = leadingTokenLabel(b.name);
    if (aTokenLabel != null !== (bTokenLabel != null)) return aTokenLabel != null ? -1 : 1;
    if (aTokenLabel != null && bTokenLabel != null) return nameCollator.compare(aTokenLabel, bTokenLabel);
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
}
function sortByIncludedServices(a, b) {
    var _ref, _ref1;
    var _a_bundle, _b_bundle;
    // TODO: Fix this impl.
    const aServices = (_ref = (_a_bundle = a.bundle) === null || _a_bundle === void 0 ? void 0 : _a_bundle.services.map((param)=>{
        let { name } = param;
        return name;
    }).join(',').toLowerCase()) !== null && _ref !== void 0 ? _ref : '';
    const bServices = (_ref1 = (_b_bundle = b.bundle) === null || _b_bundle === void 0 ? void 0 : _b_bundle.services.map((param)=>{
        let { name } = param;
        return name;
    }).join(',').toLowerCase()) !== null && _ref1 !== void 0 ? _ref1 : '';
    return aServices < bServices ? -1 : aServices > bServices ? 1 : 0;
}
function sortByPrice(a, b) {
    const [aPrice, bPrice] = [
        a,
        b
    ].map((service)=>utils.applyDiscount(serviceTemplateModel.isFixedPrice(service.cost) || serviceTemplateModel.isVariablePrice(service.cost) ? service.cost.price : service.cost.minPrice != null ? service.cost.minPrice : service.cost.maxPrice, service.cost.discount));
    return aPrice.minus(bPrice).toNumber();
}
function sortByFrequency(a, b) {
    const [aFrequency, bFrequency] = [
        a,
        b
    ].map(serviceTemplateModel.extendedRecurrencePeriod);
    return aFrequency < bFrequency ? -1 : aFrequency > bFrequency ? 1 : 0;
}
function sortByBilling(a, b) {
    const [aBilling, bBilling] = [
        a,
        b
    ].map((service)=>serviceTemplateModel.isManual(service.billing) ? 'manual' : 'automatic');
    return aBilling < bBilling ? -1 : aBilling > bBilling ? 1 : 0;
}
function sortByTaxable(a, b) {
    return Number(isServiceTaxable(a)) - Number(isServiceTaxable(b));
}
function sortByQboStatus(a, b, qboItemsById) {
    var _qboItemsById_serviceTemplateModel_integratedId, _qboItemsById_serviceTemplateModel_integratedId1;
    if (!serviceTemplateModel.integratedId({
        serviceTemplate: a,
        accounting: Accounting.QBO
    })) return serviceTemplateModel.integratedId({
        serviceTemplate: b,
        accounting: Accounting.QBO
    }) ? 1 : 0;
    if (!serviceTemplateModel.integratedId({
        serviceTemplate: b,
        accounting: Accounting.QBO
    })) return -1;
    const aName = (_qboItemsById_serviceTemplateModel_integratedId = qboItemsById[serviceTemplateModel.integratedId({
        serviceTemplate: a,
        accounting: Accounting.QBO
    })]) === null || _qboItemsById_serviceTemplateModel_integratedId === void 0 ? void 0 : _qboItemsById_serviceTemplateModel_integratedId.name.toLowerCase();
    const bName = (_qboItemsById_serviceTemplateModel_integratedId1 = qboItemsById[serviceTemplateModel.integratedId({
        serviceTemplate: b,
        accounting: Accounting.QBO
    })]) === null || _qboItemsById_serviceTemplateModel_integratedId1 === void 0 ? void 0 : _qboItemsById_serviceTemplateModel_integratedId1.name.toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
}
export const sortFunctions = {
    ["Service name"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByName(a, b),
    ["rate"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByPrice(a, b) || sortByName(a, b),
    ["billingOccurance"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByFrequency(a, b) || sortByName(a, b),
    ["billingTrigger"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByBilling(a, b) || sortByName(a, b),
    ["includedServices"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByIncludedServices(a, b),
    ["tax"]: (isAsc)=>(a, b)=>(isAsc ? -1 : 1) * sortByTaxable(a, b) || sortByName(a, b),
    ["qboStatus"]: (isAsc, qboItemsById)=>(a, b)=>(isAsc ? -1 : 1) * sortByQboStatus(a, b, qboItemsById) || sortByName(a, b)
};
function isUniqueServiceName(param) {
    let { serviceName, skipServiceId, allServices } = param;
    return !allServices || allServices.every((service)=>service.name !== serviceName || service.id === skipServiceId);
}
export const serviceNameTextsPath = 'billing.serviceName';
export function validateServiceName(param) {
    let { serviceName, skipServiceId, allServices, texts } = param;
    if (!serviceName) {
        return texts.noServiceName();
    }
    if (!isUniqueServiceName({
        serviceName,
        allServices,
        skipServiceId
    })) {
        return texts.provideUniqueServiceName();
    }
    return true;
}
export function getServiceNameValidation(param) {
    let { skipServiceId, allServices, texts, validators } = param;
    return composeValidators([
        validators.titleValidation(texts.label(), {
            required: texts.pleaseEnterServiceName()
        }),
        {
            validate: {
                serviceName: (serviceName)=>validateServiceName({
                        serviceName,
                        skipServiceId,
                        allServices,
                        texts
                    })
            }
        }
    ]);
}
export function getDuplicatedService(param) {
    let { service, serviceNames, validators, nameSuffix } = param;
    const suffix = nameSuffix;
    const newService = serviceTemplateModel.clone(service);
    const titleValidator = validators.getTitleValidator();
    const getIsValid = ()=>titleValidator(newService.name) === true;
    const getIsNotUnique = ()=>serviceNames.some((name)=>name === newService.name);
    // Verify uniqueness and keep adding 'new's until a unique name is received.
    do {
        newService.name += suffix;
    }while (getIsNotUnique());
    // Truncate the name if it is too long.
    let isValid = getIsValid();
    let isNotUnique = getIsNotUnique();
    while(!isValid || isNotUnique){
        const match = newService.name.match(new RegExp(suffix));
        const suffixIndex = match === null || match === void 0 ? void 0 : match.index;
        if (suffixIndex == null) throw new Error('Suffix not found in service name');
        // Remove last char before ' new' suffix.
        newService.name = newService.name.slice(0, suffixIndex - 1) + newService.name.slice(suffixIndex);
        isValid = getIsValid();
        isNotUnique = getIsNotUnique();
    }
    return newService;
}
export function getDuplicatedServices(param) {
    let { services, allServices, validators, nameSuffix } = param;
    const duplicatedServices = [];
    services.forEach((service)=>{
        const serviceNames = duplicatedServices.concat(allServices || []).map((s)=>s.name);
        duplicatedServices.push(getDuplicatedService({
            service,
            serviceNames,
            validators,
            nameSuffix
        }));
    });
    return duplicatedServices;
}
export const triggerLabelsTextsPath = 'billing.triggerLabels';
export function billingTriggerLabel(billing, texts) {
    const manual = ()=>texts.manual();
    const automatic = ()=>texts.automatic();
    return serviceTemplateModel.matchBilling(billing, {
        ongoing: automatic,
        onApproval: automatic,
        repeatableManual: manual,
        oneTimeManual: manual,
        onDemand: manual,
        oneTimeInStages: (param)=>{
            let { oneTimeInStages } = param;
            return serviceBillingModel.stagesTriggerLabel(oneTimeInStages, texts);
        }
    });
}
export const occurrenceTextsPath = 'billing.occurrence';
export function occurrenceLabel(billing, texts, recurrencePeriodsTexts) {
    const recurring = (recurringBilling)=>getRecurrencePeriodLabels(recurrencePeriodsTexts)[recurringBilling.recurrencePeriod];
    const oneTime = ()=>texts.oneTime();
    return serviceTemplateModel.matchBilling(billing, {
        ongoing: recurring,
        repeatableManual: recurring,
        oneTimeManual: oneTime,
        onApproval: oneTime,
        oneTimeInStages: ()=>texts.inStages(),
        onDemand: ()=>texts.onDemand()
    });
}
export const servicesColumnsWithEditableRate = (param)=>{
    let { columns, onEditRate } = param;
    return columns.map((column)=>column.name === 'rate' ? _object_spread_props(_object_spread({}, column), {
            isEditable: true,
            onEdit: onEditRate,
            onEditBi: {
                name: 'service_library_edit_rate'
            }
        }) : column);
};
